import { SaveEstimateDto } from '@/api/estimate.api';
import { SelectedEstimateOption } from '@/types/estimates/SelectedEstimateOptionType';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { useCallback } from 'react';
import { SubmitHandler } from 'react-hook-form';
import {
  useSaveEstimate,
  useSaveEstimateAsClient,
  useSendReservationRequest,
  useSendReservationRequestAsClient,
} from './useEstimate';
import {
  roundUpPrice,
  sanitizeDeltaPrice,
} from '@/utils/circuits/prices/formatPrice';
import { useToast } from './useToast';
import { Room } from '@/utils/estimates/findAllRoomCombinations';
import { EstimateResponse } from '@/types/estimates/EstimateResponse';
import { toHotelPensionType } from '@/constants/hotelPension';
import { parseReservationErrorCode } from '@/utils/estimates/reservationError';
import { validateEstimateReservationParticipants } from '@/types/estimates/EstimateFormData';
import { useTranslation } from '@/i18n';

type SubmitFormType = (
  arrivalDate: Date,
  isReservation?: boolean,
  selectedOptions?: Record<string, SelectedEstimateOption>,
) => SubmitHandler<EstimateFormData>;

interface UseSubmitEstimateProps {
  projectId: string;
  estimateId: string;
  accessToken?: string;
  pricesByStartDate: Map<string, number>;
  setIsReservationRequestAcceptedModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setIsReservationBlockedModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  adults: EstimateResponse['adults'];
  children: EstimateResponse['children'];
  isAgencyView?: boolean;
  isToView?: boolean;
  isClientLink?: boolean;
}

export const useSubmitEstimate = ({
  projectId,
  estimateId,
  accessToken,
  pricesByStartDate,
  setIsReservationRequestAcceptedModalOpen,
  setIsReservationBlockedModalOpen,
  adults,
  children,
  isAgencyView = false,
  isToView = false,
  isClientLink = false,
}: UseSubmitEstimateProps) => {
  const { mutateAsync: saveEstimate } = useSaveEstimate(projectId, estimateId);
  const { mutateAsync: sendReservationRequest } = useSendReservationRequest(
    projectId,
    estimateId,
  );
  const { mutateAsync: saveEstimateAsClient } = useSaveEstimateAsClient(
    projectId,
    estimateId,
    accessToken,
  );
  const { mutateAsync: sendReservationRequestAsClient } =
    useSendReservationRequestAsClient(projectId, estimateId, accessToken);

  const { toastAsync } = useToast();
  const { t } = useTranslation();

  const submitForm: SubmitFormType = useCallback(
    (
      arrivalDate: Date,
      isReservation = false,
      selectedOptions?: Record<string, SelectedEstimateOption>,
    ) =>
      async (data) => {
        // Contrôle des noms d'adultes désactivé pour le flux TO (devis agence
        // et devis TO) — à réactiver éventuellement plus tard. Conservé pour le
        // distributeur classique.
        if (isReservation && !isClientLink && !isAgencyView && !isToView) {
          const participantError =
            validateEstimateReservationParticipants(data);
          if (participantError) {
            await toastAsync(Promise.reject(new Error(participantError)), {
              error: participantError,
            });
            return;
          }
        }

        const selectedOptionsToSave =
          selectedOptions ?? data.selectedEstimateOptions;

        const totalAmountWithoutFlight =
          (pricesByStartDate.get(arrivalDate.toISOString()) ?? 0) +
          Object.values(selectedOptionsToSave).reduce(
            (acc, { deltaPrice, carCategoryOptions }) =>
              acc +
              sanitizeDeltaPrice(deltaPrice ?? 0) +
              Object.values(carCategoryOptions ?? {})
                .map((categoryElement) =>
                  categoryElement
                    .filter(({ isRequired }) => !isRequired) // Required options are already included in the circuit price
                    .reduce(
                      (acc, { deltaPrice }) =>
                        acc + sanitizeDeltaPrice(deltaPrice),
                      0,
                    ),
                )
                .reduce((acc, val) => acc + val, 0),
            0,
          );
        const estimateDto: SaveEstimateDto = {
          daysTitles: data.circuit.circuitDays.map(
            ({ stepTitle }) => stepTitle ?? '',
          ),
          daysDescriptions: data.circuit.circuitDays.map(
            ({ description }) => description ?? '',
          ),
          summary: data.circuit.summary,
          importantPoints: data.circuit.importantPoints,
          priceIncludes: data.circuit.priceIncludes,
          priceExcludes: data.circuit.priceExcludes,
          modificationsConditions: data.circuit.modificationsConditions,
          cancellationConditions: data.circuit.cancellationConditions,
          amount: roundUpPrice(totalAmountWithoutFlight),
          arrivalDate,
          mainContact: data.mainContact,
          options: Object.entries(selectedOptionsToSave).map(
            ([stepId, selectedOptionInfo]) => ({
              stepId,
              estimateOptionInfo: {
                deltaPrice: sanitizeDeltaPrice(selectedOptionInfo.deltaPrice),
                type: selectedOptionInfo.type,
                benefitId: selectedOptionInfo.benefitId,
                carCategoryId: selectedOptionInfo.carCategoryId,
                hotelCategoryId: selectedOptionInfo.hotelCategoryId,
                roomCombination: selectedOptionInfo.roomCombination?.map(
                  (room) => ({
                    room: room as Room,
                  }),
                ),
                hotelPensionType: selectedOptionInfo.hotelPensionType
                  ? toHotelPensionType(selectedOptionInfo.hotelPensionType)
                  : undefined,
                carCategoryInsurances:
                  selectedOptionInfo.carCategoryOptions?.insurances,
                carCategoryOneWayFees:
                  selectedOptionInfo.carCategoryOptions?.oneWayFees,
                carCategoryOptions:
                  selectedOptionInfo.carCategoryOptions?.options,
                carCategoryTaxes: selectedOptionInfo.carCategoryOptions?.taxes,
              },
            }),
          ),
          customParagraphTitle: data.customParagraphTitle,
          customParagraphDescription: data.customParagraphDescription,
          internationalFlightsPrice: data.internationalFlightsPrice,
          internationalFlightsNonMarginedTaxes:
            data.internationalFlightsNonMarginedTaxes,
          internationalFlightsMarginRate: data.internationalFlightsMarginRate,
          marginRateExistsForInternationalFlights:
            data.marginRateExistsForInternationalFlights !== undefined
              ? data.marginRateExistsForInternationalFlights === 'true'
                ? true
                : false
              : undefined,
          // Agency overlay fields are only meaningful in the AGENCY view; the
          // backend guards them, so we only attach them when isAgencyView.
          ...(isAgencyView
            ? {
                agencyMarginRate: data.agencyMarginRate,
                agencyInternationalFlightsPrice:
                  data.agencyInternationalFlightsPrice,
                agencyInternationalFlightsNonMarginedTaxes:
                  data.agencyInternationalFlightsNonMarginedTaxes,
                agencyInternationalFlightsMarginRate:
                  data.agencyInternationalFlightsMarginRate,
                agencyMarginRateExistsForInternationalFlights:
                  data.agencyMarginRateExistsForInternationalFlights !==
                  undefined
                    ? data.agencyMarginRateExistsForInternationalFlights ===
                      'true'
                    : undefined,
                agencyAdditionalMarginType: data.agencyAdditionalMarginType,
                agencyAdditionalMarginValue: data.agencyAdditionalMarginValue,
              }
            : {}),
          adults: data.adults.map(({ firstName, lastName }, indexAdult) => ({
            id: adults[indexAdult]?.id,
            firstName,
            lastName,
          })),
          children: data.children.map(
            ({ firstName, lastName, birthDate }, indexChild) => ({
              id: children[indexChild]?.id,
              firstName,
              lastName,
              birthDate,
            }),
          ),
        };
        if (!isReservation)
          await toastAsync(
            accessToken
              ? saveEstimateAsClient(estimateDto)
              : saveEstimate(estimateDto),
            {
              success: 'Devis enregistré avec succès',
            },
          );
        else {
          try {
            const { status } = await toastAsync(
              accessToken
                ? sendReservationRequestAsClient(estimateDto)
                : sendReservationRequest(estimateDto),
              {
                success: 'Demande de réservation envoyée avec succès',
              },
            );
            if (status >= 200 && status < 300)
              setIsReservationRequestAcceptedModalOpen(true);
          } catch (error) {
            if (
              parseReservationErrorCode(error) ===
              'RESERVATION_BLOCKED_CIRCUIT_DELETED'
            ) {
              setIsReservationBlockedModalOpen(true);
              return;
            }
            throw error;
          }
        }
      },
    [
      accessToken,
      adults,
      pricesByStartDate,
      children,
      isAgencyView,
      isToView,
      isClientLink,
      saveEstimate,
      saveEstimateAsClient,
      sendReservationRequest,
      sendReservationRequestAsClient,
      setIsReservationBlockedModalOpen,
      setIsReservationRequestAcceptedModalOpen,
      setIsReservationBlockedModalOpen,
      t,
      toastAsync,
    ],
  );

  return { submitForm };
};
